import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../App';
import { StatusBadge, ListSkeleton, EmptyState, PageHeader } from '../components/shared';
import { Users, ChevronDown, ChevronRight, Briefcase, FileText, Activity } from 'lucide-react';

export default function PeopleRoles() {
  const [activeTab, setActiveTab] = useState('people');
  const [people, setPeople] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRole, setExpandedRole] = useState(null);
  const [roleProcedures, setRoleProcedures] = useState({});
  const [roleActivities, setRoleActivities] = useState({});
  const [allProcedures, setAllProcedures] = useState([]);
  const [allActivities, setAllActivities] = useState([]);

  useEffect(() => {
    Promise.all([
      fetch(`${BACKEND_URL}/api/people`).then(r => r.json()),
      fetch(`${BACKEND_URL}/api/roles`).then(r => r.json()),
      fetch(`${BACKEND_URL}/api/procedures`).then(r => r.json()),
      fetch(`${BACKEND_URL}/api/activities`).then(r => r.json()),
    ]).then(([peopleData, rolesData, procData, actData]) => {
      setPeople(peopleData.people || []);
      setRoles(rolesData.roles || []);
      setAllProcedures(procData.procedures || []);
      setAllActivities(actData.activities || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const toggleRole = (roleId) => {
    if (expandedRole === roleId) { setExpandedRole(null); return; }
    setExpandedRole(roleId);
    
    const role = roles.find(r => r.id === roleId);
    if (role) {
      const procs = allProcedures.filter(p => role.procedure_ids.includes(p.id));
      const acts = allActivities.filter(a => role.activity_ids.includes(a.id));
      setRoleProcedures(prev => ({ ...prev, [roleId]: procs }));
      setRoleActivities(prev => ({ ...prev, [roleId]: acts }));
    }
  };

  const getInitials = (name) => name.split(' ').map(n => n[0]).join('').toUpperCase();

  return (
    <div className="page-container" data-testid="people-roles">
      <PageHeader title="People & Roles" subtitle="Team members and organisational roles" />

      {/* Tabs */}
      <div className="flex gap-1 p-1 rounded-xl bg-muted mb-6 w-fit">
        {['people', 'roles'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === tab ? 'bg-card shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'
            }`}
            data-testid={`people-tab-${tab}`}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {loading ? <ListSkeleton /> : (
        <>
          {/* People Tab */}
          {activeTab === 'people' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {people.map(person => (
                <div key={person.id} className="ps-card-static p-6" data-testid={`person-card-${person.id}`}>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center text-sm font-bold"
                      style={{ background: 'hsl(var(--primary))', color: 'hsl(var(--primary-foreground))' }}>
                      {getInitials(person.name)}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-sm font-bold">{person.name}</h3>
                      <p className="text-xs text-muted-foreground">{person.position_title}</p>
                    </div>
                  </div>
                  <div className="mt-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <StatusBadge status={person.status} />
                      {person.start_date && (
                        <span className="text-xs text-muted-foreground">
                          Since {new Date(person.start_date).toLocaleDateString('en-AU', { month: 'short', year: 'numeric' })}
                        </span>
                      )}
                    </div>
                    {person.primary_role && (
                      <div className="pt-2 border-t border-border">
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Primary Role</p>
                        <span className="inline-block px-2.5 py-1 rounded-full text-xs font-medium" style={{ background: 'hsl(var(--primary))', color: 'hsl(var(--primary-foreground))' }}>
                          {person.primary_role}
                        </span>
                      </div>
                    )}
                    {person.secondary_roles?.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Secondary Roles</p>
                        <div className="flex flex-wrap gap-1">
                          {person.secondary_roles.map((r, i) => r && (
                            <span key={i} className="inline-block px-2 py-0.5 rounded-full text-xs bg-muted text-muted-foreground">{r}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Roles Tab */}
          {activeTab === 'roles' && (
            <div className="space-y-4">
              {roles.map(role => (
                <div key={role.id} className="ps-card-static overflow-hidden" data-testid={`role-card-${role.id}`}>
                  <button
                    onClick={() => toggleRole(role.id)}
                    className="w-full p-5 flex items-center gap-4 text-left hover:bg-muted/30 transition-colors"
                  >
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'hsl(var(--accent))' }}>
                      <Briefcase size={20} className="text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-bold">{role.name}</h3>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                        {role.primary_people?.length > 0 && (
                          <span>Primary: {role.primary_people.join(', ')}</span>
                        )}
                        {role.secondary_people?.length > 0 && (
                          <span>Secondary: {role.secondary_people.join(', ')}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1"><FileText size={12} />{role.procedure_ids?.length || 0}</span>
                      <span className="flex items-center gap-1"><Activity size={12} />{role.activity_ids?.length || 0}</span>
                      {expandedRole === role.id ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </div>
                  </button>
                  {expandedRole === role.id && (
                    <div className="px-5 pb-5 pt-2 border-t border-border">
                      {role.purpose && (
                        <p className="text-sm text-muted-foreground mb-4">{role.purpose}</p>
                      )}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Linked Procedures ({roleProcedures[role.id]?.length || 0})</h4>
                          {roleProcedures[role.id]?.length > 0 ? (
                            <div className="space-y-1">
                              {roleProcedures[role.id].map(p => (
                                <div key={p.id} className="text-sm flex items-center gap-2">
                                  <span className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">{p.name.match(/^P\d+/)?.[0]}</span>
                                  <span className="truncate">{p.name.replace(/^P\d+\s*[–—-]\s*/, '')}</span>
                                </div>
                              ))}
                            </div>
                          ) : <p className="text-xs text-muted-foreground">No linked procedures</p>}
                        </div>
                        <div>
                          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Linked Activities ({roleActivities[role.id]?.length || 0})</h4>
                          {roleActivities[role.id]?.length > 0 ? (
                            <div className="space-y-1">
                              {roleActivities[role.id].map(a => (
                                <div key={a.id} className="text-sm">{a.name}</div>
                              ))}
                            </div>
                          ) : <p className="text-xs text-muted-foreground">No linked activities</p>}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
